import React from 'react';
import {
  Box,
  Typography,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tooltip,
  InputAdornment
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { useStudio } from '../../Context/StudioContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { getDesignLabel } from '../../Context/DesignLabels';
import flagEN from '../../Assets/united-kingdom.png';
import flagBM from '../../Assets/malaysia.png';
import dayjs from 'dayjs';
import DateLogic from '../../utils/DateLogic';

const Page1Settings = () => {

  const { formData, setFormData } = useStudio();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedDesignCode = formData.designCode || 'floral-001';
  const selectedDesignLabel = getDesignLabel(selectedDesignCode);


  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleToggleChange = (field) => (_, value) => {
    if (value !== null) {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const defaultCardText = {
    BM: {
      openingText: 'Assalamualaikum W.B.T',
      greetingText: "Dengan penuh kesyukuran kehadrat Ilahi, \nkami menjemput \nDato' | Datin | Tuan | Puan | Encik | Cik \nke majlis perkahwinan anakanda kami",
      closingText: 'Semoga kehadiran para tetamu memeriahkan lagi majlis ini serta mendapat keberkatan daripada Allah S.W.T.',
      eventType: 'Majlis Perkahwinan',
      day: 'Sabtu',
      dressCode: 'Formal / Tradisional',
    },
    EN: {
      openingText: 'Assalamualaikum W.B.T',
      greetingText: "With joyful hearts and gratitude to Allah S.W.T, \nwe cordially invite you to the wedding celebration of our beloved child",
      closingText: 'Your presence and blessings would mean the world to us. We look forward to celebrating this special day with you.',
      eventType: 'Wedding Celebration',
      day: 'Saturday',
      dressCode: 'Formal / Traditional',
    },
  };

  const handleLanguageChange = (lang) => {
    const text = defaultCardText[lang] || defaultCardText['BM'];
    setFormData((prev) => {
      let day = text.day;
      if (/^\d{4}-\d{2}-\d{2}$/.test(prev.eventDate)) {
        const englishDay = dayjs(prev.eventDate).format('dddd');
        day = DateLogic.toDay(englishDay, lang);
      }
      return {
        ...prev,
        language: lang,
        ...text,
        day,
      };
    });
  };


  const toggleStyle = {

    mb: 2,

    '&.Mui-selected': {
      backgroundColor: '#e0f5f4',
    },
    '&.Mui-selected:hover': {
      backgroundColor: '#e0f5f4',
    },
  };

  return (
    <Box>
      <Typography mb={1} variant="h6" gutterBottom>
        Jenis Kad
      </Typography>

      {/* Bahasa Kad */}
      <Typography variant="subtitle2" gutterBottom>
        Bahasa Kad
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>

        <Box
          onClick={() => handleLanguageChange('BM')}
          sx={{
            flex: 1,
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            cursor: 'pointer',
            borderRadius: '14px',
            background: formData.language === 'BM' ? '#e0f5f4' : '#ffffffff',
            p: 1.5,
            transition: '0.2s',
            boxShadow: '0px 3px 8px rgba(0, 0, 0, 0.1)'
          }}
        >
          <img src={flagBM} alt="Bahasa Melayu" style={{ width: '20px', height: '20px' }} />
          Bahasa Melayu
        </Box>

        <Box
          onClick={() => handleLanguageChange('EN')}
          sx={{
            flex: 1,
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            cursor: 'pointer',
            borderRadius: '14px',
            background: formData.language === 'EN' ? '#e0f5f4' : '#ffffffff',
            p: 1.5,
            transition: '0.2s',
            boxShadow: '0px 3px 8px rgba(0, 0, 0, 0.1)'
          }}
        >
          <img src={flagEN} alt="English" style={{ width: '20px', height: '20px' }} />
          English
        </Box>
      </Box>

      {/* Pakej Pilihan — card picker */}
      {(() => {
        const packages = [
          {
            id: 'Basic',
            price: 'RM29',
          },
          {
            id: 'Standard',
            price: 'RM39',
          },
          {
            id: 'Premium',
            price: 'RM49',
          },
        ];
        const selected = formData.packageType || 'Basic';

        if (formData.status === 'ACTIVE') {
          return null;
        }

        return (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Pakej Pilihan
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              {packages.map((pkg) => {
                const isActive = selected === pkg.id;
                return (
                  <Box
                    key={pkg.id}
                    onClick={() => setFormData((prev) => ({ ...prev, packageType: pkg.id }))}
                    sx={{
                      flex: 1,
                      cursor: 'pointer',
                      borderRadius: '14px',
                      background: isActive ? '#e0f5f4' : '#ffffffff',
                      p: 2,
                      transition: '0.2s',
                      boxShadow: '0px 3px 8px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    {/* Package name */}
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: isActive ? '#1f1f1fff' : '#1b9a9b', letterSpacing: '0.5px', display: 'block' }}
                    >
                      {pkg.id.toUpperCase()}
                    </Typography>

                    {/* Price */}
                    <Typography
                      sx={{
                        fontSize: '1.1rem',
                        fontWeight: 800,
                        color: '#1f1f1fff',
                        mt: 0.3,
                        mb: 1,
                        lineHeight: 1,
                      }}
                    >
                      {pkg.price}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })()}


      {/* Kod Rekaan */}
      <Box sx={{ mt: 2.5, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Kod Rekaan
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderRadius: '12px',
            background: '#ffffffff',
            p: 1.5,
            border: '1px solid rgba(0, 0, 0, 0.23)'
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 86,
              borderRadius: '12px',
              overflow: 'hidden',
              background: formData.themeBaseCardColour || '#f7f7f7',
              flexShrink: 0,
            }}
          >
            {formData.cardBackground && (
              <img
                src={formData.cardBackground}
                alt={formData.designCode || 'Selected design'}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: '#1b9a9b',
                fontWeight: 700,
                letterSpacing: '0.5px',
              }}
            >
              Selected Design
            </Typography>
            <Typography sx={{ color: '#1f1f1fff' }}>
              {selectedDesignLabel}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Code: {selectedDesignCode.toUpperCase()}
            </Typography>

          </Box>
          <Box
            onClick={() => {
              const searchParams = new URLSearchParams(location.search);
              const id = searchParams.get('id');
              const slug = searchParams.get('slug');

              const queryParams = new URLSearchParams();
              if (id) queryParams.set('id', id);
              if (slug) queryParams.set('slug', slug);
              const queryString = queryParams.toString();
              navigate(`/catalog${queryString ? `?${queryString}` : ''}`);
            }}
            sx={{
              flexShrink: 0,
              cursor: 'pointer',
              borderRadius: '999px',
              background: '#55C2C3',
              color: 'white',
              px: 2,
              py: 1,
              fontSize: '0.75rem',
              letterSpacing: '0.3px',
              whiteSpace: 'nowrap',
              transition: '0.2s',
              '&:hover': {
                background: '#44B0B1',
              },
            }}
          >
            Change
          </Box>
        </Box>
      </Box>


      {/* Gaya Pembukaan & Color */}
      <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel>Jenis Bukaan Kad</InputLabel>
            <Select
              value={formData.openingStyle || 'Tingkap A'}
              label="Gaya Pembukaan"
              onChange={handleChange('openingStyle')}
            >
              <MenuItem value="Tingkap A">Tingkap A</MenuItem>
              <MenuItem value="Tingkap B">Tingkap B</MenuItem>
              <MenuItem value="Tingkap C">Tingkap C</MenuItem>
              <MenuItem value="Tingkap D">Tingkap D</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <TextField
            type="color"
            label=""
            value={formData.openingColor || '#f2f2f2'}
            onChange={handleChange('openingColor')}
            fullWidth
            InputProps={{
              sx: { height: 56, padding: 0 },
            }}
          />

        </Grid>
      </Grid>

      <TextField
        label="Butang Buka"
        type="text"
        fullWidth
        placeholder="Contoh: Adam & Hawa"
        value={formData.openCardName}
        onChange={handleChange("openCardName")} // separate handler
        margin="normal"
        sx={{ mt: 4 }}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title="Format 2 pasangan: Adam/Hawa & Aiman/Anis" placement="top" arrow>
                <InfoOutlined fontSize="small" sx={{ color: 'text.secondary', cursor: 'pointer' }} />
              </Tooltip>
            </InputAdornment>
          ),
        }}
      />
      {/* Animasi Efek & Color */}
      {/* <Grid container spacing={2} alignItems="center" sx={{ mt: 2 }}>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel>Jenis Animasi</InputLabel>
            <Select
              value={formData.animationEffect || 'Salji'}
              label="Jenis Animasi"
              onChange={handleChange('animationEffect')}
            >
              <MenuItem value="Salji">Salji</MenuItem>
              <MenuItem value="Kelip">Kelip</MenuItem>
              <MenuItem value="Love">Love</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <TextField
            type="color"
            label=""
            value={formData.animationColor || '#51638a'}
            onChange={handleChange('animationColor')}
            fullWidth
            InputProps={{
              sx: { height: 56, padding: 0 },
            }}
          />
        </Grid>
      </Grid> */}


    </Box>
  );
};

export default Page1Settings;
